'use client';

import { useMemo } from 'react';
import { FaShareAlt } from 'react-icons/fa';
import { MdLocationOn } from 'react-icons/md';

import { Button } from '@/components/Button';
import { CategoryIcon } from '@/components/Store/StoreCard';
import * as StoreMap from '@/components/StoresMap';
import { usePageTextContent } from '@/context/pageTextContent';
import * as Layout from '@/layout';
import { ClientConfig } from '@/services/clientConfig';
import { captureException } from '@/utils/error';

import { AboutUs } from './AboutUs';
import { BuyGiftcard } from './BuyGiftcard';
import { Contact } from './Contact';
import { ImagesGrid } from './ImagesGrid';
import { Networks } from './Networks';
import { Tags } from './Tags';

export type StoreProfilePageProps = {
  store: AdaptedStore<true>;
  category: Category | null;
  secondaryCategory: Category | null;
};

export function StoreProfilePageBase({
  store,
  category,
  secondaryCategory,
}: StoreProfilePageProps) {
  const { content } = usePageTextContent<StoreProfilePageContent>();

  const { shareButton } = content.profile;

  const { streetAddress, zipcode, city } = store.address;

  const address = `${streetAddress}, ${zipcode} ${city}`;

  const clientConfig = ClientConfig.getInstance().getConfig();

  const displayNetworks = useMemo(() => {
    if (!store.isActive) return [];

    return store.networks.filter(network => {
      if (network.networkType === 'ASSOCIATION') return false;

      if (clientConfig.storeIgnoreNetworkConnectionIds.includes(network.id)) {
        return false;
      }

      return network.canRedeem && !network.ghostModeForRedeems;
    });
  }, [store.networks]);

  const categoriesNames = useMemo(() => {
    if (!category) return '';

    if (!secondaryCategory) return category.name;

    return `${category.name} & ${secondaryCategory.name}`;
  }, [category, secondaryCategory]);

  const onShare = async () => {
    try {
      if (!navigator.share) return;

      await navigator.share({ url: window.location.href });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      captureException({ error, tags: { browser: true } });
    }
  };

  return (
    <Layout.Section className="bg-gray-50">
      <Layout.SectionContent className="flex flex-col items-start space-y-8">
        <div className="w-full flex-col items-start space-y-2">
          <h1 className="text-2xl">{store.name}</h1>

          <div className="flex flex-col gap-4 md:flex-row">
            <div className="flex items-center space-x-1">
              <MdLocationOn className="text-store-primary text-xl" />

              <span className="text-sm">{address}</span>
            </div>

            <div className="flex items-center gap-2">
              <CategoryIcon
                className="self-center"
                parentCategoryId={store.parentCategoryId}
              />

              <span className="text-sm">{categoriesNames}</span>
            </div>

            <div className="flex items-center">
              <Button
                className="gap-2 p-0 font-normal"
                onClick={onShare}
                variant="ghost"
              >
                <FaShareAlt className="text-store-primary text-xl" />

                <span className="text-sm">{shareButton}</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-[60%_37%] lg:gap-10">
          <ImagesGrid store={store} content={content} />

          <div className="size-full h-[70vh] overflow-hidden rounded-md shadow-lg lg:h-auto">
            <StoreMap.Map
              stores={[store]}
              content={{
                enableCookieMessage: content.profile.maptilerCookiesInstruction,
                mapInstructionsMobile: content.profile.mapInstructionsMobile,
                mapZoomInstructions: content.profile.mapZoomInstructions,
                mapPlaceholderUrl: content.profile.mapPlaceholderImageUrl,
              }}
              initialZoom={14}
              openStorePreviewOnClick={false}
            >
              <StoreMap.Controls.Zoom className="top-4 right-3.5 lg:top-4 lg:right-3.5" />
            </StoreMap.Map>
          </div>

          <div>
            <div className="flex flex-col items-start space-y-5">
              <AboutUs storeProfile={store.profile} content={content} />

              <hr className="w-full border-white" />

              <Contact
                storeProfile={store.profile}
                address={address}
                content={content}
              />

              <hr className="w-full border-white" />

              <Tags tags={store.profile.tags} content={content} />

              <hr className="w-full border-white" />

              <Networks
                store={store}
                displayNetworks={displayNetworks}
                content={content}
              />
            </div>
          </div>

          {store.isActive && (
            <div>
              <BuyGiftcard
                store={store}
                displayNetworks={displayNetworks}
                content={content}
              />
            </div>
          )}
        </div>
      </Layout.SectionContent>
    </Layout.Section>
  );
}
